import React from 'react';
import {FormikErrors, withFormik} from 'formik';
import PropTypes from 'prop-types';
import {InferPropTypes} from 'commons/types/Types';
import {FieldShape} from 'commons/shapes';
import {ShareholderForm} from 'commons/models/dtos/clients/Client';
import {formValidator, setDefaultValue, validateDateonRFCandCURP} from 'commons/utils/Functions';
import InnerFormShareholderPersonalInfo from './InnerFormShareholderPersonalInfo';
import {Messages} from 'commons/constants';

ShareholderPersonalInfoForm.propTypes = {
    auxShareholder: PropTypes.any,
    viewId: PropTypes.number.isRequired,
    fieldHandler: PropTypes.func.isRequired,
    submitHandler: PropTypes.func.isRequired,
    currentPercentage: PropTypes.number.isRequired,
    accumulatedPercentage: PropTypes.number.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape(FieldShape).isRequired).isRequired,
};

function validatePercentage(
    participationPercent: number,
    globalPercentage: number,
    currentPercentage: number,
    errors: any
) {
    if (Number(participationPercent) + Number(globalPercentage) - Number(currentPercentage) > 100) {
        errors.participationPercent =
            'El porcentaje de participaci\u00F3n total no puede superar el 100%';
    } else if (participationPercent > 99.99) {
        errors.participationPercent =
            'El porcentaje de participaci\u00F3n para un accionista no debe superar el 100%';
    } else if (Number(participationPercent) <= 0) {
        errors.participationPercent = Messages.INVALID_PARTICIPATION_PERCENTAGE;
    }
}

const FormControl = withFormik<
    InferPropTypes<typeof ShareholderPersonalInfoForm.propTypes>,
    ShareholderForm
>({
    mapPropsToErrors: () => {
        return {
            expedientId: '',
            workflowId: '',
            viewId: '',
            viewFatherId: '',
            kycId: '',
            personTypeKey: '',
            rfc: '',
            participationPercent: '',
            name: '',
            lastName: '',
            secondLastName: '',
            birthDate: '',
            rol: '',
            fatherKycId: '',
            businessDescription: '',
            protocolizationDate: '',
            countryKey: '',
            municipalityDescription: '',
            municipalityKey: '',
            stateDescription: '',
            outdoorNumber: '',
            stateKey: '',
            streetDescription: '',
            addressDescription: '',
            zipCode: '',
            addressId: '',
            colonyDescription: '',
            colonyKey: '',
            interiorNumber: '',
            foreignCitizen: '',
            addressOpt: '',
            hasNext: '',
        };
    },
    mapPropsToValues: (props) => {
        const {auxShareholder} = props;
        if (auxShareholder) {
            return {
                expedientId: setDefaultValue(auxShareholder.expedientId, 0),
                workflowId: setDefaultValue(auxShareholder.workflowId, 0),
                viewId: setDefaultValue(auxShareholder.viewId, 0),
                viewFatherId: setDefaultValue(auxShareholder.viewFatherId, 0),
                kycId: setDefaultValue(auxShareholder.kycId, 0),
                personTypeKey: setDefaultValue(auxShareholder.personTypeKey, ''),
                rfc: setDefaultValue(auxShareholder.rfc, ''),
                participationPercent: setDefaultValue(auxShareholder.participationPercent, 0),
                name: setDefaultValue(auxShareholder.name, ''),
                lastName: setDefaultValue(auxShareholder.lastName, ''),
                secondLastName: setDefaultValue(auxShareholder.secondLastName, ''),
                birthDate: setDefaultValue(auxShareholder.birthDate, ''),
                rol: setDefaultValue(auxShareholder.rol, ''),
                fatherKycId: setDefaultValue(auxShareholder.fatherKycId, 0),
                businessDescription: setDefaultValue(auxShareholder.businessDescription, ''),
                protocolizationDate: setDefaultValue(auxShareholder.protocolizationDate, ''),
                countryKey: setDefaultValue(auxShareholder.countryKey, ''),
                municipalityDescription: setDefaultValue(
                    auxShareholder.municipalityDescription,
                    ''
                ),
                municipalityKey: setDefaultValue(auxShareholder.municipalityKey, ''),
                stateDescription: setDefaultValue(auxShareholder.stateDescription, ''),
                outdoorNumber: setDefaultValue(auxShareholder.outdoorNumber, ''),
                stateKey: setDefaultValue(auxShareholder.stateKey, ''),
                streetDescription: setDefaultValue(auxShareholder.streetDescription, ''),
                addressDescription: setDefaultValue(auxShareholder.addressDescription, ''),
                zipCode: setDefaultValue(auxShareholder.zipCode, ''),
                addressId: setDefaultValue(auxShareholder.addressId, 1),
                colonyDescription: setDefaultValue(auxShareholder.colonyDescription, ''),
                colonyKey: setDefaultValue(auxShareholder.colonyKey, ''),
                interiorNumber: setDefaultValue(auxShareholder.interiorNumber, ''),
                addressOpt: setDefaultValue(auxShareholder.addressOpt, false),
                foreignCitizen: auxShareholder.foreignCitizen,
                nif: auxShareholder.nif,
                hasNext: (auxShareholder.hasNext, false),
            };
        } else {
            return {
                expedientId: 0,
                workflowId: 0,
                viewId: 0,
                viewFatherId: 0,
                kycId: 0,
                personTypeKey: '',
                rfc: '',
                participationPercent: 0,
                name: '',
                lastName: '',
                secondLastName: '',
                birthDate: '',
                rol: '',
                fatherKycId: 0,
                businessDescription: '',
                protocolizationDate: '',
                countryKey: '',
                municipalityDescription: '',
                municipalityKey: '',
                stateDescription: '',
                outdoorNumber: '',
                stateKey: '',
                streetDescription: '',
                addressDescription: '',
                zipCode: '',
                addressId: null,
                colonyDescription: '',
                colonyKey: '',
                interiorNumber: '',
                foreignCitizen: false,
                addressOpt: false,
                hasNext: false,
            };
        }
    },
    validateOnBlur: true,
    validateOnChange: true,
    validate: (values: ShareholderForm, props) => {
        const errors: FormikErrors<ShareholderForm> = {};
        formValidator(props.fields, errors, values);
        if (values.participationPercent) {
            validatePercentage(
                values.participationPercent,
                props.accumulatedPercentage,
                props.currentPercentage,
                errors
            );
        }
        if (!values.foreignCitizen) {
            if (values.rfc && values.birthDate) {
                if (!validateDateonRFCandCURP(values.birthDate, values.rfc))
                    errors.rfc = Messages.RFC_NOT_MATCH_WITH_DBO;
            }
        }
        return errors;
    },
    handleSubmit: (values, actions) => {
        actions.setSubmitting(false);
        actions.props.submitHandler({...values});
    },
})(InnerFormShareholderPersonalInfo);
function ShareholderPersonalInfoForm(
    props: InferPropTypes<typeof ShareholderPersonalInfoForm.propTypes>
) {
    const {
        fields,
        viewId,
        fieldHandler,
        submitHandler,
        auxShareholder,
        currentPercentage,
        accumulatedPercentage,
    } = props;
    /* eslint-disable react/jsx-handler-names */
    return (
        <FormControl
            viewId={viewId}
            fields={fields}
            fieldHandler={fieldHandler}
            submitHandler={submitHandler}
            auxShareholder={auxShareholder}
            currentPercentage={currentPercentage}
            accumulatedPercentage={accumulatedPercentage}
        />
    );
}

export default ShareholderPersonalInfoForm;
